/**
 * Opłacalność instalacji przy net‑billingu — czysta arytmetyka, bez Reacta.
 *
 * DLACZEGO TO W OGÓLE LICZYMY, skoro `mocKalkulator.js` świadomie tego nie robi:
 * tam użytkownik dopiero szacuje moc i nie zna żadnej ceny — obietnica zwrotu byłaby
 * wróżeniem. Tutaj ma już wybrany zestaw z cenami produktów i sam podaje kwotę
 * instalacji. To inne pytanie, zadane w innym momencie.
 *
 * Zasady, które trzymają to po stronie rachunku, a nie obietnicy:
 *  1. Każda stała jest nazwana, wyeksportowana i pokazywana użytkownikowi.
 *  2. Wynik podajemy dla WIDEŁEK kosztu, nigdy dla jednej kwoty.
 *  3. Scenariusz bez wzrostu cen energii liczymy zawsze obok domyślnego.
 *
 * ═══ SEDNO NET‑BILLINGU ═══
 * Energia zużyta na miejscu oszczędza pełną zmienną cenę zakupu według taryfy klienta (ok. 1 zł/kWh
 * z dystrybucją). Nadwyżka oddana do sieci wraca jako pieniądze w depozycie, wyceniona rynkowo razy
 * 1,23 (ok. 0,30–0,33 zł/kWh), i płaci tylko za energię kupioną z sieci, nie za dystrybucję. Kilowatogodzina
 * zużyta u siebie jest więc warta ok. trzy razy tyle, co oddana, i z tego bierze się cały sens magazynu
 * oraz to, że dokładanie modułów ponad własne zużycie zwraca się coraz wolniej. Szczegóły rozliczenia:
 * `lib/rozliczenie.js`, ceny: `config/taryfy.js` i `config/cenyEnergii.js`.
 */

import { prognoza } from './prognozaProdukcji'
import {
  CENA_ZAKUPU_ZL_KWH, CENA_SPRZEDAZY_ZL_KWH, WZROST_CEN_DOMYSLNY, UDZIAL_WZROSTU_CENY_SPRZEDAZY,
} from '../config/cenyEnergii'
import { rozliczenieZCen, wartoscEnergii } from './rozliczenie'

/**
 * Ceny energii i założenia net-billingu mieszkają w `config/cenyEnergii.js` i `config/taryfy.js` (jedno
 * miejsce dla kalkulatora mocy, opłacalności, Projektu 3D i oferty). Tu tylko je udostępniamy pod dawnymi
 * nazwami, bo czytają je ekrany założeń.
 */
export {
  CENA_ZAKUPU_ZL_KWH, CENA_SPRZEDAZY_ZL_KWH, WZROST_CEN_DOMYSLNY,
}

export const LATA_ANALIZY = 25
/** Falownik jest jedynym elementem, który niemal na pewno wymieni się w trakcie życia instalacji. */
export const ROK_WYMIANY_FALOWNIKA = 12
/** Gdy nie znamy ceny falownika z zestawu — typowy udział w koszcie całości. */
export const UDZIAL_FALOWNIKA = 0.12

/**
 * Ulga termomodernizacyjna: odliczenie od podstawy, więc korzyść zależy od stawki podatku.
 * 19% to podatek liniowy (działalność gospodarcza), przy którym ulga też przysługuje.
 */
export const PROGI_ULGI = [
  { key: 'brak', label: 'Nie korzystam', stawka: 0 },
  { key: 'p12', label: 'PIT 12%', stawka: 0.12 },
  { key: 'p19', label: 'Podatek liniowy 19%', stawka: 0.19 },
  { key: 'p32', label: 'PIT 32%', stawka: 0.32 },
]
export const LIMIT_ULGI_ZL = 53000

/** Polski miks energetyczny — do przeliczenia produkcji na uniknioną emisję. */
export const EMISJA_KG_CO2_KWH = 0.7

/**
 * Podpowiedź kosztu instalacji „pod klucz" — w złotych na kWp, bo tak wycenia rynek.
 *
 * DLACZEGO NIE JAKO NARZUT NA CENĘ PRODUKTÓW: pierwsza wersja tego kodu mnożyła sumę
 * z zestawu przez 1,45–1,85 i przy instalacji 7,9 kWp podpowiadała 12,5–16 tys. zł,
 * czyli ok. 1800 zł/kWp. To nieprawda — moduły i falownik to zaledwie ok. 30% kwoty
 * końcowej, reszta to konstrukcja, okablowanie, zabezpieczenia, robocizna,
 * dokumentacja i marża wykonawcy. Zaniżony koszt dawał zwrot w 3 lata zamiast
 * realnych 7–8 i cała sekcja przestawała być wiarygodna.
 *
 * Widełki obejmują typowy rozrzut ofert dla mikroinstalacji domowej; przy bardzo
 * małych instalacjach cena jednostkowa jest wyższa (koszty stałe rozkładają się
 * na mniej kilowatów), dlatego poniżej 5 kWp dokładamy narzut.
 */
export const KOSZT_MONTAZU_MIN_ZL_KWP = 3200
export const KOSZT_MONTAZU_MAX_ZL_KWP = 4200
export const KOSZT_MAGAZYNU_MIN_ZL_KWH = 2200
export const KOSZT_MAGAZYNU_MAX_ZL_KWH = 3200

export const podpowiedzKosztu = (kwp, pojemnoscKwh = 0) => {
  if (!kwp) return { od: null, do: null }
  // Poniżej 5 kWp koszty stałe (przyłącze, dokumentacja, dojazd) rozkładają się
  // na mniej kilowatów, więc cena jednostkowa rośnie.
  const narzutMalej = kwp < 5 ? 1 + (5 - kwp) * 0.05 : 1
  const zaokr500 = (v) => Math.round(v / 500) * 500
  return {
    od: zaokr500(kwp * KOSZT_MONTAZU_MIN_ZL_KWP * narzutMalej
                 + pojemnoscKwh * KOSZT_MAGAZYNU_MIN_ZL_KWH),
    do: zaokr500(kwp * KOSZT_MONTAZU_MAX_ZL_KWP * narzutMalej
                 + pojemnoscKwh * KOSZT_MAGAZYNU_MAX_ZL_KWH),
  }
}

const zaokr = (v) => Math.round(v)

/**
 * Wartość rocznej produkcji w złotówkach z samych sum rocznych (`prognoza(...).rocznie`), dla jednej ceny
 * zakupu (G11). Pełny rachunek z taryfą i miesiącami: `wartoscEnergii` w `lib/rozliczenie.js`.
 *
 * Bez znanego zużycia nie da się rozdzielić autokonsumpcji od nadwyżki, a że różnią się ceną ok.
 * trzykrotnie, wynik byłby zmyślony. Zwracamy wtedy `null`.
 */
export const wartoscRoczna = (rocznie, ceny = {}) => wartoscEnergii({ rocznie }, rozliczenieZCen(ceny))

/**
 * Korzyść z ulgi termomodernizacyjnej dla danego wydatku.
 *
 * Odliczyć można tylko wydatek, którego nie pokryła dotacja (ustawa o PIT wyłącza część
 * sfinansowaną z dotacji), więc podstawą jest koszt MINUS dotacja, nie mniej niż zero, i dopiero ona idzie pod limit.
 * Wcześniej liczyliśmy ulgę od całego kosztu i osobno odejmowali dotację: ta sama złotówka
 * zmniejszała nakład dwa razy, a zwrot wychodził za krótki.
 */
export const korzysciZUlgi = (koszt, progKey, dotacjaZl = 0) => {
  const prog = PROGI_ULGI.find((p) => p.key === progKey) || PROGI_ULGI[0]
  const podstawa = Math.max(0, (koszt || 0) - (dotacjaZl || 0))
  return Math.min(podstawa, LIMIT_ULGI_ZL) * prog.stawka
}

/**
 * Przepływy przez cały okres analizy.
 *
 * Co roku liczymy prognozę od nowa, a nie mnożymy pierwszego roku przez współczynnik:
 * degradacja modułów zmienia nie tylko sumę produkcji, ale też PROPORCJĘ autokonsumpcji
 * do nadwyżki (mniejsza produkcja = większy jej udział zużyty na miejscu). Skrót przez
 * mnożnik zaniżałby wartość późnych lat.
 *
 * `rozliczenie` (wynik `rozliczenieKlienta`): taryfa i ceny klienta. Bez niego dawne `ceny = { zakup,
 * sprzedaz }`, czyli G11 z jedną ceną (domyślną, gdy pusto).
 *
 * Każdy rok w `lata` niesie też pola tabeli „rok po roku” (jak w analizie ekonomicznej projektu):
 * zużycie, koszt prądu bez paneli, energia zużyta u siebie i oddana, depozyt, który realnie wrócił,
 * ceny tego roku i suma oszczędności od startu.
 */
export const przeplywy = ({
  koszt,
  wejscie,
  kosztFalownikaZl = null,
  wzrostCen = WZROST_CEN_DOMYSLNY,
  ulga = 'brak',
  dotacjaZl = 0,
  ceny = {},
  rozliczenie = null,
}) => {
  const r = rozliczenie || rozliczenieZCen(ceny)
  // Ulga liczona od kosztu po dotacji i odliczona w roku 0, razem z nakładem (uproszczenie:
  // w praktyce wraca z rozliczeniem PIT za rok zakupu).
  const ulgaZl = korzysciZUlgi(koszt, ulga, dotacjaZl)
  const nakladNetto = Math.max(0, koszt - ulgaZl - (dotacjaZl || 0))
  const wymiana = kosztFalownikaZl != null ? kosztFalownikaZl : koszt * UDZIAL_FALOWNIKA

  const lata = []
  let skumulowany = -nakladNetto
  let sumaOszczednosci = 0

  for (let rok = 1; rok <= LATA_ANALIZY; rok += 1) {
    const p = prognoza({ ...wejscie, rok })
    const w = wartoscEnergii(p, r, {
      zakup: (1 + wzrostCen) ** (rok - 1),
      // Cena rynkowa też rośnie, ale wolniej niż detaliczna (patrz config/cenyEnergii).
      sprzedaz: (1 + wzrostCen * UDZIAL_WZROSTU_CENY_SPRZEDAZY) ** (rok - 1),
    })
    if (!w) return null

    const koszty = rok === ROK_WYMIANY_FALOWNIKA ? wymiana : 0
    const przeplyw = w.razemZl - koszty
    skumulowany += przeplyw
    sumaOszczednosci += w.razemZl

    lata.push({
      rok,
      produkcjaKwh: zaokr(p.rocznie.produkcjaKwh),
      przychodZl: zaokr(w.razemZl),
      kosztyZl: zaokr(koszty),
      przeplywZl: zaokr(przeplyw),
      skumulowanyZl: zaokr(skumulowany),
      zuzycieKwh: zaokr(p.rocznie.zuzycieKwh),
      autokonsumpcjaKwh: zaokr(p.rocznie.autokonsumpcjaKwh),
      nadwyzkaKwh: zaokr(p.rocznie.nadwyzkaKwh),
      kosztBezPvZl: zaokr(w.kosztBezPvZl),
      zOszczednosciZl: zaokr(w.zOszczednosciZl),
      depozytZl: zaokr(w.zeSprzedazyZl),
      sumaOszczednosciZl: zaokr(sumaOszczednosci),
      cenaZakupuZlKwh: Math.round(w.cenaZakupuZlKwh * 100) / 100,
      wartoscOddanejZlKwh: Math.round(w.wartoscOddanejZlKwh * 100) / 100,
    })
  }

  // Moment przejścia przez zero — z interpolacją w obrębie roku, bo „7 lat" i „prawie
  // 8 lat" to dla kogoś, kto podejmuje decyzję, zauważalna różnica.
  let zwrotLata = null
  for (let i = 0; i < lata.length; i += 1) {
    if (lata[i].skumulowanyZl >= 0) {
      const przed = i === 0 ? -nakladNetto : lata[i - 1].skumulowanyZl
      const przyrost = lata[i].skumulowanyZl - przed
      zwrotLata = przyrost > 0
        ? Math.round((lata[i].rok - 1 + (-przed) / przyrost) * 10) / 10
        : lata[i].rok
      break
    }
  }

  return {
    nakladNettoZl: zaokr(nakladNetto),
    ulgaZl: zaokr(ulgaZl),
    wymianaFalownikaZl: zaokr(wymiana),
    lata,
    zwrotLata,
    zyskPo25LatachZl: lata[lata.length - 1].skumulowanyZl,
    oszczednosci25LatZl: zaokr(sumaOszczednosci),
  }
}

/**
 * Ten sam dom z magazynem i bez.
 *
 * DLACZEGO TO JEST WŁAŚCIWE PYTANIE: magazyn podnosi autokonsumpcję, a kilowatogodzina
 * zużyta u siebie jest warta ponad trzy razy tyle, co sprzedana — więc bateria realnie
 * zarabia. Ale kosztuje, i to dużo. Który wariant wygrywa, zależy od profilu zużycia
 * i ceny baterii, a tego nikt nie policzy z głowy. Dlatego pokazujemy oba obok siebie,
 * zamiast doradzać jeden.
 *
 * Gdy zestaw NIE MA magazynu, podpowiadamy pojemność ok. 1 kWh na kWp — to środek
 * widełek, w których magazyn zdąży się naładować i nie stoi pusty (patrz mocKalkulator).
 */
export const porownajZMagazynem = ({ koszt, wejscie, pojemnoscKwh, ...reszta }) => {
  const maMagazyn = (wejscie.pojemnoscMagazynuKwh || 0) > 0
  const pojemnosc = maMagazyn
    ? wejscie.pojemnoscMagazynuKwh
    : (pojemnoscKwh || Math.round(wejscie.kwp * 10) / 10)

  // Koszt samego magazynu — środek widełek cenowych za kWh zainstalowany.
  const kosztMagazynu = pojemnosc
    * (KOSZT_MAGAZYNU_MIN_ZL_KWH + KOSZT_MAGAZYNU_MAX_ZL_KWH) / 2

  const zMag = przeplywy({
    koszt: maMagazyn ? koszt : koszt + kosztMagazynu,
    wejscie: { ...wejscie, pojemnoscMagazynuKwh: pojemnosc },
    ...reszta,
  })
  const bezMag = przeplywy({
    koszt: maMagazyn ? Math.max(0, koszt - kosztMagazynu) : koszt,
    wejscie: { ...wejscie, pojemnoscMagazynuKwh: 0 },
    ...reszta,
  })
  if (!zMag || !bezMag) return null

  return {
    pojemnoscKwh: pojemnosc,
    kosztMagazynuZl: Math.round(kosztMagazynu),
    maMagazynWZestawie: maMagazyn,
    zMagazynem: zMag,
    bezMagazynu: bezMag,
  }
}

/**
 * Zestawienie dla widełek kosztu — dolna i górna granica podanej kwoty.
 *
 * Jedna liczba zwrotu przy kwocie podanej „mniej więcej" udawałaby precyzję,
 * której w danych nie ma.
 */
export const zakresZwrotu = ({ kosztOd, kosztDo, ...reszta }) => {
  const tani = przeplywy({ koszt: kosztOd, ...reszta })
  const drogi = przeplywy({ koszt: kosztDo, ...reszta })
  if (!tani || !drogi) return null
  return { tani, drogi }
}
